/**
 * SourcesLine (ARCH-005 §8, AI-09, DES-05).
 *
 * Renders the structured SourceCitation[] as one terracotta line below the
 * meta-note. Terracotta is the learning-layer signal and is used ONLY here
 * (plus the operator content queue, not in scope) per DES-05. please-family
 * sources link to the public framework page; external sources link out.
 *
 * This component is shared and is rendered by DeliverableLayout, never by a
 * per-type renderer (ARCH-005 §3 / Don't).
 */

import type { Source } from '@studio/api-schemas';

export function SourcesLine({
  sources,
  fallbackLine,
}: {
  sources: Source[];
  /** The pre-rendered meta_notes.sources_line string, used when the
   *  structured array is empty (the operator may have only written the line). */
  fallbackLine?: string | null;
}) {
  if (sources.length === 0) {
    if (!fallbackLine || fallbackLine.trim().length === 0) return null;
    return (
      <p className="text-sm leading-relaxed text-[color:var(--color-terracotta)]">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">sources </span>
        {fallbackLine}
      </p>
    );
  }

  return (
    <p className="text-sm leading-relaxed text-[color:var(--color-terracotta)]">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">sources </span>
      {sources.map((s, i) => (
        <span key={`${s.ref}-${i}`}>
          {i > 0 ? ' · ' : ''}
          {s.type === 'external' && isUrl(s.ref) ? (
            <a
              href={s.ref}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              {s.label}
            </a>
          ) : (
            <span title={s.ref}>{s.label}</span>
          )}
        </span>
      ))}
    </p>
  );
}

function isUrl(ref: string): boolean {
  return /^https?:\/\//i.test(ref);
}
